import React, { useEffect, useState, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState } from "draft-js";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardBody, Col, Container, Row, Button, Form, Label, UncontrolledTooltip, FormFeedback } from "reactstrap";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Breadcrumbs from "components/Common/BreadcrumbTitle";
import CommonDialog from "components/Common/CommonDialog";
import { getNoteById, updateNotes } from "http/fundLync/entityAndAddress.helper";
import { downloadSystemConfigFile } from "http/UAM/systemConfig.helper";
import ToastDialog from "components/Common/ToastDialog";
import { useDropzone } from "react-dropzone";

import { API_URL } from "http/url.helper";

const EditEntityNote = ({ editNoteId, onUpdate, onCancel }) => {
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [showCommonDialog, setShowCommonDialog] = useState({ isShow: false,showSubmit: true});
  const [files, setFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [toastInfo, setToastInfo] = useState({ message: "", showToast: false, role: "" });
  const [uploadProgress, setUploadProgress] = useState({}); // Track upload progress
  const [errorMessage, setErrorMessage] = useState(""); // Track upload errors

  useEffect(() => {
    if (editNoteId) {
      fetchNoteById(editNoteId);
    }
  }, [editNoteId]);

  const fetchNoteById = async (editNoteId) => {
    try {
      const fetchedNote = await getNoteById({ noteId: editNoteId });
      setNote(fetchedNote);
      setFiles(fetchedNote.additionalDocuments[0]?.file_path || []);
      const contentState = ContentState.createFromText(fetchedNote.notes);
      setEditorState(EditorState.createWithContent(contentState));
    } catch (error) {
      console.error("Error fetching note:", error);
    }
  };

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    const allowedExtensions = [
      ".jpg",
      ".png",
      ".jpeg",
      ".pdf",
      ".xlsx",
      ".xls",
    ];

    const filteredFiles = acceptedFiles.filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      return allowedExtensions.includes(`.${fileExtension}`);
    });

    if (filteredFiles.length < acceptedFiles.length) {
      let rejectedFiles = acceptedFiles.filter((file) => !filteredFiles.includes(file));
      rejectedFiles = rejectedFiles
        .map((file) => file.name)
        .join(", ");
        const errorMessage = `The following files are not supported: ${rejectedFiles}. Only (.jpg, .png, .jpeg, .pdf, .xlsx, .xls) files are allowed.`;
      setShowCommonDialog({ isShow: true, message: errorMessage,showSubmit: false});
    }

    if (filteredFiles.length > 0) {
      uploadFile(filteredFiles)
        .then((uploadedFiles) => {
          setNewFiles((prevFiles) => [
            ...prevFiles,
            ...filteredFiles.map((file, index) => ({
              name: file.name,
              path: uploadedFiles[index],
            })),
          ]);
        })
        .catch((error) => {
          setErrorMessage(error.message);
          setShowCommonDialog({ isShow: true, message: error.message,showSubmit: false });
        });
    }
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({onDrop,
    accept: ".jpg, .png, .jpeg, .pdf, .xlsx, .xls",
  });

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setNewFiles([...newFiles, ...selectedFiles]);
  };

  const removeFile = (index, isNew = false) => {
    if (isNew) {
      setNewFiles(newFiles.filter((_, i) => i !== index));
    } else {
      setFiles(files.filter((_, i) => i !== index));
    }
  };

  const uploadFile = async (files) => {
    const uploadPromises = files.map(
      
      (file) =>
        
        new Promise((resolve, reject) => {
          if (file) {
            setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
            let formData = new FormData();
            formData.append("upl", file);
            const config = {
              onUploadProgress: (progressEvent) => {
                const percentage = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                setUploadProgress((prev) => ({
                  ...prev,
                  [file.name]: percentage,
                }));
              },
            };
            axios
              .post(`${API_URL}/s3/uploadSystemConfigFiles`, formData, config)
              .then((response) => {
                if (response.status === 200) {
                  resolve(response.data[0].fileName);
                } else {
                  reject(response.data.message);
                }
              })
              .catch((error) => {
                reject(error.response?.data?.message || error.message);
              });
          } else {
            reject("File is undefined or null");
          }
        })
    );

    try {
      const uploadedUrls = await Promise.all(uploadPromises);
      return uploadedUrls;
    } catch (error) {
      throw new Error(error);
    }
  };

  const validationSchema = Yup.object().shape({
    content: Yup.string().test(
      "content",
      "Notes can't be empty",
      value => value && value.trim() !== ""
    )
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { content: editorState.getCurrentContent().getPlainText(), fileUpload: [] },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setShowCommonDialog({ isShow: true, message: "Are you sure you want to update the note?" ,showSubmit: true});
    },
  });

  useEffect(() => {
    validation.setFieldValue('content', editorState.getCurrentContent().getPlainText());
  }, [editorState]);

  const dialogAction = async (action) => {
    if (action === 1 && note) {
      const content = editorState.getCurrentContent().getPlainText();
      try {
        const data = {
          notes: content,
          id: note.id,
          existingFiles: files.map((file) => file), 
          newFiles: newFiles.map((file) =>file.path)   
        };
  
        await updateNotes(data);
  
        onUpdate();
      } catch (error) {
        console.error("Error updating note:", error);
      }
    } else {
      setShowCommonDialog({ isShow: false,showSubmit: true});
    }
  };
  

  const editorStyle = {
    overflow: 'hidden'
  };

  const formatFunction = (file, showToastCallback) => {
    const str = file.substring(file.indexOf("com") + 4);
    const filename = file.substring(file.lastIndexOf("/") + 1);
    const ext = str.split(".").pop();
    const obj = {
      fileName: str,
    };

    downloadSystemConfigFile(obj)
      .then((response) => {
        if (response.code === 200) {
          downloadFile(response.result, ext, filename);
          showToastCallback({
            message: "Downloaded successfully",
            showToast: true,
            role: "success",
          });
        } else {
          showToastCallback({
            message: response.message,
            showToast: true,
            role: "error",
          });
        }
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
      });
  };

  const downloadFile = (base64String, ext, fileName) => {
    let source = "";
    if (ext === "zip") {
      source = `data:application/zip;base64,${base64String}`;
    } else {
      source = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64String}`;
    }
    const link = document.createElement("a");
    link.href = source;
    link.download = fileName;
    link.click();
  };

  const extractFileName = (url) => {
    const segments = url.split("/");
    return decodeURIComponent(segments[segments.length - 1]);
  };

  const handleToast = (info) => {
    setToastInfo(info);
  };

  return (
    <React.Fragment>
      {/* <div className="page-content" style={{ marginTop: "-90px" }}> */}
      <Container fluid>
        <Row className="vertical-scroll">
          <Col xs="12">
            <div>
              <div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                  }}
                >
                  <Row>
                    <Col sm={12} className="editorWrapper">
                      <Editor
                        editorState={editorState}
                        onEditorStateChange={handleEditorChange}
                        toolbarHidden={false}
                        wrapperClassName="editorWrapper"
                        editorClassName="editorClass"
                      />
                      {validation.errors.content && (
                        <div className="text-danger">
                          {validation.errors.content}
                        </div>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="12">
                      <div>
                        <div className="d-flex flex-column">
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="col-form-label margin-auto"
                          >
                            Upload File (.xlsx, .xls, .jpg, .png, .jpeg, .pdf)
                          </Label>
                          <div
                            className="drop" {...getRootProps()}
                          >
                            <div  className="text-wrapper">
                              <div className="centered">
                                <input
                                  type="file"
                                  multiple
                                  name="formatDocument"
                                  id="itrFileExcel"
                                  accept=".jpg, .png, .jpeg, .pdf, .xlsx, .xls"
                                  {...getInputProps()}
                                />
                                {/* <label
                                  style={{ marginBottom: 0, fontSize: "14px" }}
                                  htmlFor="itrFileExcel"
                                > */}
                                  <img
                                    src="https://scfuat.cashinvoice.in/frontend/assets/themes/image/icon/cloud.png"
                                    className="img-fluid"
                                    style={{ width: "30px" }}
                                    alt="Upload"
                                  />
                                  {isDragActive && (
                                    <p className="textLink">Drop here</p>
                                  )}
                                  <span className="textLink">
                                    Browse files to attach
                                  </span>{" "}
                                  or Drop
                                {/* </label> */}
                              </div>
                            </div>
                          </div>

                          {validation.values.fileUpload.length > 0 && (
                            <ul className="mt-2">
                              {validation.values.fileUpload.map(
                                (file, index) => (
                                  <li key={index}  className="documents-item">
                                    {file.name}
                                    {uploadProgress[file.name] > 0 &&
                                      !errorMessage && (
                                        <div className="progress mt-2">
                                          <div
                                            className="progress-bar bg-success"
                                            role="progressbar"
                                            style={{
                                              width: `${
                                                uploadProgress[file.name]
                                              }%`,
                                            }}
                                            aria-valuenow="0"
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                          >
                                            {`${uploadProgress[file.name]}%`}
                                          </div>
                                        </div>
                                      )}
                                    {errorMessage && (
                                      <div className="alert alert-danger mt-2">
                                        {errorMessage}
                                      </div>
                                    )}
                                  </li>
                                )
                              )}
                            </ul>
                          )}
                          {validation.touched.fileUpload &&
                            validation.errors.fileUpload && (
                              <FormFeedback
                                type="invalid"
                                style={{ display: "block" }}
                              >
                                {validation.errors.fileUpload}
                              </FormFeedback>
                            )}
                        </div>
                      </div>
                    </Col>
                    <Col sm="5">
                      <div className="documents-container mt-3">
                        <ul className="documents-list">
                          {files.map((file, index) => (
                            <li key={index} className="documents-item mb-0">
                              <span
                                className="truncated-filename"
                                id={`file-tooltip-${index}`}
                              >
                                {extractFileName(file)}
                              </span>
                              <UncontrolledTooltip
                                placement="top"
                                target={`file-tooltip-${index}`}
                              >
                                {extractFileName(file)}
                              </UncontrolledTooltip>
                              <div>
                                <i
                                  className="mdi mdi-download"
                                  style={{
                                    fontSize: "20px",
                                    cursor: "pointer",
                                    color: "#34c38f",
                                  }}
                                  onClick={() =>
                                    formatFunction(file, handleToast)
                                  }
                                  id={`download-tooltip-${index}`}
                                ></i>
                                <UncontrolledTooltip
                                  placement="top"
                                  target={`download-tooltip-${index}`}
                                >
                                  Download
                                </UncontrolledTooltip>
                                <i
                                  className="mdi mdi-close"
                                  style={{
                                    fontSize: "20px",
                                    cursor: "pointer",
                                    color: "#f66b68",
                                    fontWeight: "800",
                                  }}
                                  onClick={() => removeFile(index)}
                                ></i>
                              </div>
                            </li>
                          ))}
                          {newFiles.map((file, index) => (
                            <li key={index} className="documents-item mb-0">
                              <span
                                className="truncated-filename"
                                id={`newfile-tooltip-${index}`}
                              >
                                {file.name}
                              </span>
                              <UncontrolledTooltip
                                placement="top"
                                target={`newfile-tooltip-${index}`}
                              >
                                {file.name}
                              </UncontrolledTooltip>
                              <i
                                className="mdi mdi-close"
                                style={{
                                  fontSize: "20px",
                                  cursor: "pointer",
                                  color: "#f66b68",
                                  fontWeight: "800",
                                }}
                                onClick={() => removeFile(index, true)}
                              ></i>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="text-end" style={{ margin: "20px 0" }}>
                        <Button
                          type="button"
                          outline
                          color="success"
                          onClick={onCancel}
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          style={{
                            backgroundColor: "#21aa47",
                            borderColor: "#21aa47",
                            marginLeft: "7px",
                          }}
                        >
                          Update
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      {/* </div> */}
      {showCommonDialog.isShow && (
        <CommonDialog
          isShow={showCommonDialog.isShow}
          message={showCommonDialog.message}
          postitiveBtn={showCommonDialog.showSubmit ? "Submit" : undefined}
          negativeBtn={"Close"}
          onCloseFn={dialogAction}
        />
      )}
      <ToastDialog
        message={toastInfo.message || ""}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      />
    </React.Fragment>
  );
};

export default EditEntityNote;
